import { useState, useRef, useCallback } from 'react';
import { dashboardHandler, CustomDateParams, createCancelSource, isRequestCancelled, CancelTokenSource } from '../DashboardHandler';
import { mapActiveUsersResponse } from '../utils/mappers';
import type { ActiveUserInfo, ActiveUsersResponse, CustomDateRange } from '../types';
import { getComparisonDays } from '../Components/DateRangePicker';

interface UseActiveUsersReturn {
  users: ActiveUserInfo[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  page: number;
  pageSize: number;
  fetchActiveUsers: (period: string, page: number, customRange?: CustomDateRange) => void;
  reset: () => void;
}

export const useActiveUsers = (): UseActiveUsersReturn => {
  const [users, setUsers] = useState<ActiveUserInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const cancelSourceRef = useRef<CancelTokenSource | null>(null);

  const fetchActiveUsers = useCallback((period: string, pageNum: number, customRange?: CustomDateRange) => {
    if (cancelSourceRef.current) {
      cancelSourceRef.current.cancel('New request started');
    }

    const source = createCancelSource();
    cancelSourceRef.current = source;

    setLoading(true);
    setError(null);

    let customDate: CustomDateParams | undefined;
    if (period === 'custom' && customRange) {
      customDate = {
        startDate: customRange.startDate,
        endDate: customRange.endDate,
        comparisonDays: getComparisonDays(customRange),
      };
    }

    dashboardHandler
      .getActiveUsers(period, pageNum, 20, source.token, customDate)
      .then((result) => {
        const mapped = mapActiveUsersResponse(result);
        setUsers(mapped.users);
        setTotalCount(mapped.totalCount);
        setPage(mapped.page);
        setPageSize(mapped.pageSize);
        setLoading(false);
      })
      .catch((err) => {
        if (!isRequestCancelled(err)) {
          console.error('Failed to fetch active users:', err);
          setError(err.message || 'Failed to load active users');
          setLoading(false);
        }
      });
  }, []);

  const reset = useCallback(() => {
    if (cancelSourceRef.current) {
      cancelSourceRef.current.cancel('Modal closed');
    }
    setUsers([]);
    setLoading(false);
    setError(null);
    setTotalCount(0);
    setPage(1);
  }, []);

  return { users, loading, error, totalCount, page, pageSize, fetchActiveUsers, reset };
};
